/**
 * Sidebar Navigation Component
 * Main navigation sidebar for AccountsGenie dashboard
 * Following PRD Phase 2 specifications with connection status integration
 */

import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useConnectionStore } from '../../stores/connectionStore';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';
import { smartPreloader } from '../lazy';
import {
  LayoutDashboard,
  MessageSquare,
  FileText,
  BarChart3,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Wifi,
  WifiOff,
  Loader2,
  User,
  HelpCircle,
  Wrench,
  Receipt
} from 'lucide-react';
import NotificationPanel from '../ui/notification-panel';
import { HistoryDropdown } from '../sidebar/RecentRuns';

interface NavigationItem {
  id: string;
  label: string;
  path: string;
  icon: React.ElementType;
  phase: number;
  requiresConnection?: boolean;
  badge?: string | number;
  disabled?: boolean;
}

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  currentPath: string;
}

const navigationItems: NavigationItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: LayoutDashboard,
    phase: 2
  },
  {
    id: 'chat',
    label: 'AI Chat',
    path: '/chat',
    icon: MessageSquare,
    phase: 3,
    requiresConnection: false, // Enable chat without Tally connection for debugging
    disabled: false // Phase 3 is now implemented
  },
  {
    id: 'bank-entry',
    label: 'Bank Entry',
    path: '/bank-entry',
    icon: FileText,
    phase: 1,
    requiresConnection: false,
    disabled: false
  },
  {
    id: 'invoice-entry',
    label: 'Invoice Entry',
    path: '/invoice-entry',
    icon: Receipt,
    phase: 1,
    requiresConnection: false,
    disabled: false
  },
  {
    id: 'external-tools',
    label: 'Tools',
    path: '/external-tools',
    icon: Wrench,
    phase: 4,
    disabled: false // Phase 4 now implemented
  },
  {
    id: 'audit',
    label: 'Audit Log',
    path: '/audit',
    icon: FileText,
    phase: 4,
    disabled: false // Phase 4 is now implemented
  },
  {
    id: 'reports',
    label: 'Reports',
    path: '/reports',
    icon: BarChart3,
    phase: 5,
    requiresConnection: false, // Enable reports without connection for now
    disabled: false // Phase 5 now implemented
  }
];

const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  onToggle,
  currentPath
}) => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { status: connectionStatus, checkConnection } = useConnectionStore();

  // Check Tally connection on Sidebar mount
  useEffect(() => {
    checkConnection(false).catch(err =>
      console.error('[Sidebar] Connection check failed:', err)
    );
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const getConnectionStatusIcon = () => {
    switch (connectionStatus) {
      case 'connected':
        return <Wifi className="h-4 w-4 text-green-500" />;
      case 'connecting':
        return <Loader2 className="h-4 w-4 text-yellow-500 animate-spin" />;
      default:
        return <WifiOff className="h-4 w-4 text-red-500" />;
    }
  };

  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Tally Online';
      case 'connecting':
        return 'Connecting...';
      default:
        return 'Tally Offline';
    }
  };

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'connecting':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-red-100 text-red-800 border-red-200';
    }
  };

  const isItemAccessible = (item: NavigationItem) => {
    if (item.disabled) return false;
    if (item.requiresConnection && connectionStatus !== 'connected') return false;
    return true;
  };

  const isCurrentPath = (path: string) => {
    return currentPath === path || (path === '/dashboard' && currentPath === '/');
  };

  const handleNavHover = (path: string) => {
    // Preload route on hover for better performance
    const routeName = path.replace('/', '') || 'dashboard';
    smartPreloader.preloadOnHover(routeName);
  };

  return (
    <div className={cn(
      "bg-card border-r transition-all duration-300 ease-in-out",
      "flex flex-col h-full",
      collapsed ? "w-16" : "w-64"
    )}>
      {/* Header */}
      <div className="p-4 border-b flex-shrink-0">
        <div className="flex items-center justify-between">
          {collapsed ? (
            <button
              onClick={onToggle}
              className="flex items-center justify-center mx-auto hover:opacity-80 transition-opacity p-1"
              title="Expand sidebar"
            >
              <img
                src="/logo-small.png"
                alt="AccountsGenie"
                className="h-10 w-10 object-contain"
              />
            </button>
          ) : (
            <>
              <div className="flex items-center space-x-2">
                <img
                  src="/logo-long.png"
                  alt="AccountsGenie"
                  className="h-8 object-contain"
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onToggle}
                className="h-8 w-8 p-0"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Connection Status - fixed height */}
      <div className="px-4 py-2 flex-shrink-0">
        <Card className={cn(
          "border transition-colors",
          getConnectionStatusColor()
        )}>
          <CardContent className="p-2">
            <div className="flex items-center space-x-2">
              {getConnectionStatusIcon()}
              {!collapsed && (
                <span className="text-xs font-medium truncate">
                  {getConnectionStatusText()}
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
        {navigationItems.map((item) => {
          const isActive = isCurrentPath(item.path);
          const isAccessible = isItemAccessible(item);
          const IconComponent = item.icon;

          // Determine if this item needs history dropdown
          const historyType = item.id === 'bank-entry' ? 'bank_entry'
            : item.id === 'invoice-entry' ? 'purchase'
            : null;

          return (
            <div key={item.id}>
              {isAccessible ? (
                <>
                  <Link
                    to={item.path}
                    onMouseEnter={() => handleNavHover(item.path)}
                    className={cn(
                      "flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                      "hover:bg-accent hover:text-accent-foreground",
                      isActive
                        ? "bg-accent text-accent-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    <IconComponent className="h-5 w-5 flex-shrink-0" />
                    {!collapsed && (
                      <>
                        <span className="flex-1">{item.label}</span>
                        {item.badge && (
                          <Badge variant="secondary" className="text-xs">
                            {item.badge}
                          </Badge>
                        )}
                      </>
                    )}
                  </Link>
                  {historyType && (
                    <HistoryDropdown batchType={historyType} collapsed={collapsed} />
                  )}
                </>
              ) : (
                <div
                  className={cn(
                    "flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium",
                    "text-muted-foreground/50 cursor-not-allowed"
                  )}
                  title={
                    item.requiresConnection
                      ? "Requires Tally connection"
                      : "Coming in future phases"
                  }
                >
                  <IconComponent className="h-5 w-5 flex-shrink-0" />
                  {!collapsed && (
                    <>
                      <span className="flex-1">{item.label}</span>
                      <Badge variant="outline" className="text-xs">
                        Phase {item.phase}
                      </Badge>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </nav>


      {/* Bottom Section - Notifications + User Profile */}
      <div className="border-t flex-shrink-0">
        {/* Notifications */}
        <div className={cn(
          "px-4 py-2 border-b",
          collapsed ? "flex justify-center" : "flex items-center"
        )}>
          <NotificationPanel />
          {!collapsed && <span className="ml-2 text-sm">Notifications</span>}
        </div>

        {/* User Profile with Dropdown */}
        <div className="p-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={cn(
                "w-full flex items-center rounded-lg hover:bg-accent transition-colors p-2",
                collapsed ? "justify-center" : "space-x-3"
              )}>
                <div className="h-8 w-8 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="h-4 w-4" />
                </div>
                {!collapsed && (
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium truncate">
                      {user?.name || 'User'}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {user?.email || 'user@example.com'}
                    </p>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" className="w-56">
              <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer">
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/help')} className="cursor-pointer">
                <HelpCircle className="h-4 w-4 mr-2" />
                Help & Support
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600">
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;